import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiUrl } from './api/config';
import {
    AUTH_SESSION_EXPIRED_EVENT,
    clearStoredAuthToken,
    getTokenExpirationTime,
    getUsableStoredAuthToken,
    installAuthExpiredFetchHandler,
    storeAuthToken,
} from './authSession';
import {
    buildAiSolutionFromImages as buildAiSolutionFromImagesRequest,
    buildMathBankJson as buildMathBankJsonRequest,
    extractPdfText as extractPdfTextRequest,
    fetchWordTemplates as fetchWordTemplatesRequest,
    generateWordFromBank as generateWordFromBankRequest,
    generateWordAnswerSheet as generateWordAnswerSheetRequest,
    generateWordDocument as generateWordDocumentRequest,
    parseWordDocument as parseWordDocumentRequest,
    previewWordAnswerSheet as previewWordAnswerSheetRequest,
    previewBilingualExamJob as previewBilingualExamRequest,
    previewHandwriting as previewHandwritingRequest,
    repairHandwritingImage as repairHandwritingImageRequest,
    previewMatplotlibCode as previewMatplotlibCodeRequest,
    processAiText as processAiTextRequest,
    getAiProcessJob as getAiProcessJobRequest,
    getHandoutImportJob as getHandoutImportJobRequest,
    getImageRedrawJob as getImageRedrawJobRequest,
    getPdfExtractJob as getPdfExtractJobRequest,
    listPdfExtractJobs as listPdfExtractJobsRequest,
    cancelPdfExtractJob as cancelPdfExtractJobRequest,
    removeHandwriting as removeHandwritingRequest,
    sendAiChatMessage as sendAiChatMessageRequest,
    startAiProcessJob as startAiProcessJobRequest,
    startAiSolutionFromImagesJob as startAiSolutionFromImagesJobRequest,
    startHandoutImportJob as startHandoutImportJobRequest,
    startImageRedrawJob as startImageRedrawJobRequest,
    startPdfExtractJob as startPdfExtractJobRequest,
    getAiSolutionFromImagesJob as getAiSolutionFromImagesJobRequest,
} from './api/documentProcessing';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [authToken, setAuthToken] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const token = getUsableStoredAuthToken();
        if (token) {
            setAuthToken(token);
            setIsAuthenticated(true);
        }
    }, []);

    useEffect(() => {
        const handleExpiredSession = () => {
            setAuthToken(null);
            setIsAuthenticated(false);
        };

        const uninstallFetchHandler = installAuthExpiredFetchHandler();
        window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleExpiredSession);

        return () => {
            uninstallFetchHandler();
            window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleExpiredSession);
        };
    }, []);

    useEffect(() => {
        if (!authToken) return undefined;

        const expirationTime = getTokenExpirationTime(authToken);
        if (expirationTime === null) return undefined;

        const delay = expirationTime - Date.now();
        if (delay <= 0) {
            clearStoredAuthToken();
            setAuthToken(null);
            setIsAuthenticated(false);
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            clearStoredAuthToken();
            setAuthToken(null);
            setIsAuthenticated(false);
        }, delay);

        return () => window.clearTimeout(timeoutId);
    }, [authToken]);

    const login = async (username, password, stayLoggedIn) => {
        try {
            const response = await fetch(`${apiUrl}/login/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
            const data = await response.json();
            if (response.ok) {
                setAuthToken(data.access);
                setIsAuthenticated(true);
                storeAuthToken(data.access, stayLoggedIn);
                return { success: true };
            } else {
                return { success: false, error: data.error || 'Login failed' };
            }
        } catch (error) {
            console.error('Error occurred during login:', error);
            return { success: false, error: 'Network error' };
        }
    };

    const logout = () => {
        setAuthToken(null);
        setIsAuthenticated(false);
        clearStoredAuthToken();
    };
    /*
    {
      "noteData": "這是使用者輸入的筆記內容",
      "todoData": [
        {
          "goal": "目標 1",
          "purpose": "目的 1",
          "todo": "待辦事項 1",
          "timing": "時間 1"
        },
        {
          "goal": "目標 2",
          "purpose": "目的 2",
          "todo": "待辦事項 2",
          "timing": "時間 2"
        }
        // 可以包含更多的待辦事項
      ]
    }
    */

    // Todo + Note
    const fetchTodoNoteData = async () => {
        if (!authToken) {
            console.error("No access token found. User might not be logged in.");
            return { success: false, error: "User not authenticated" };
        }

        try {
            const response = await fetch(`${apiUrl}/get-toDoNotes/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
            });

            const result = await response.json();
            if (response.ok && result.note_data !== undefined && result.todo_data !== undefined) {
                // console.log('Fetched data successfully:', result);
                return { success: true, data: result };
            } else {
                console.error('Data format is incorrect:', result);
                return { success: false, error: 'Data format is incorrect' };
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            return { success: false, error: 'Error fetching data' };
        }
    };

    const sendTodoNoteData = async (noteData, todoData) => {
        if (!authToken) {
            console.error("No access token found. User might not be logged in.");
            return { success: false, error: "User not authenticated" };
        }

        try {
            const response = await fetch(`${apiUrl}/save-toDoNotes/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({ note_data: noteData, todo_data: todoData }),
            });

            const result = await response.json();
            if (response.ok) {
                // console.log('Data sent successfully:', result);
                return { success: true, data: result };
            } else {
                console.error('Failed to send data:', result);
                return { success: false, error: result.detail || 'Failed to send data' };
            }
        } catch (error) {
            console.error('Error sending data:', error);
            return { success: false, error: 'Error sending data' };
        }
    };

    const jumpTodoHistory = async (direction) => {
        if (!authToken) {
            console.error("No access token found. User might not be logged in.");
            return { success: false, error: "User not authenticated" };
        }

        const endpoint = direction === "redo" ? "redo-toDoNotes" : "undo-toDoNotes";

        try {
            const response = await fetch(`${apiUrl}/${endpoint}/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
            });

            const result = await response.json();
            if (response.ok && result.success) {
                return { success: true, data: result.data, message: result.message, history: result.history };
            }

            return {
                success: false,
                error: result.message || result.detail || 'Failed to jump history',
                code: result.code,
                status: response.status,
            };
        } catch (error) {
            console.error('Error jumping todo history:', error);
            return { success: false, error: 'Error jumping todo history' };
        }
    };

    // Bulletins
    const getBulletins = async () => {
        try {
            const response = await fetch(`${apiUrl}/get-bulletin/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch bulletins: ${response.status}`);
            }
            const bulletins = await response.json();
            // console.log('Fetched bulletins:', bulletins);
            return bulletins;
        } catch (error) {
            console.error('Error fetching bulletins:', error);
            return [];
        }
    };

    const saveBulletin = async (content) => {
        if (!authToken) {
            console.error("No access token found. User might not be logged in.");
            return { success: false, error: "User not authenticated" };
        }

        try {
            const response = await fetch(`${apiUrl}/save-bulletin/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({ content })
            });
            if (!response.ok) {
                throw new Error('Failed to save bulletin');
            }
            const result = await response.json();
            // console.log('Bulletin saved successfully:', result);
            return result;
        } catch (error) {
            console.error('Error saving bulletin:', error);
            return { success: false, error: 'Error saving bulletin' };
        }
    };

    const deleteBulletin = async (id) => {
        if (!authToken) {
            console.error("No access token found. User might not be logged in.");
            return { success: false, error: "User not authenticated" };
        }

        try {
            const response = await fetch(`${apiUrl}/delete-bulletin/${id}/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
            });
            if (response.ok) {
                // console.log('Bulletin deleted successfully');
                return { success: true };
            } else {
                const errorData = await response.json();
                console.error('Failed to delete bulletin:', errorData);
                return { success: false, error: errorData.error || 'Failed to delete bulletin' };
            }
        } catch (error) {
            console.error('Error deleting bulletin:', error);
            return { success: false, error: 'Error deleting bulletin' };
        }
    };

    // Accounting
    const fetchAccountingData = async () => {
        if (!authToken) {
            console.error("No access token found. User might not be logged in.");
            return { success: false, error: "User not authenticated" };
        }

        try {
            const response = await fetch(`${apiUrl}/get-accounting/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
            });

            const result = await response.json();
            if (response.ok && result.debt !== undefined && result.payments !== undefined) {
                const paymentsWithTimestamp = result.payments.map((payment) => ({
                        ...payment,
                        timestamp: payment.timestamp ?? payment.time, // Prefer timezone-aware ISO; support legacy responses
                    }));
                return { success: true, data: { ...result, payments: paymentsWithTimestamp } };
            } else {
                console.error('Data format is incorrect:', result);
                return { success: false, error: 'Data format is incorrect' };
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            return { success: false, error: 'Error fetching data' };
        }
    };

    const sendAccountingData = async (debtsData, paymentsData) => {
    if (!authToken) {
        console.error("Access token not found. The user might not be logged in.");
        return { success: false, error: "User not authenticated" };
    }

    // Helper function：處理金額字串，去掉非數字符號後再 parseFloat
    const parseNumeric = (value) => {
        if (typeof value === "string") {
        // 去除任何非數字/小數點/負號的字元，例如 "$"、"," 等
        const cleaned = value.replace(/[^0-9.-]+/g, "");
        return parseFloat(cleaned) || 0;
        }
        // 如果原本就是 number 或 undefined
        return parseFloat(value) || 0;
    };

    try {
        // 處理 debts 資料
        const formattedDebts = debtsData.map((debt) => {
        return {
            record_type: "debt",
            name: debt.name.trim(),
            transaction_amount: parseNumeric(debt.transaction_amount),
            order_quantity: parseInt(debt.order_quantity, 10) || 0,
            sum_price: parseNumeric(debt.sum_price),
        };
        });

        // 處理 payments 資料
        const formattedPayments = paymentsData.map((payment) => {
        return {
            record_type: "payment",
            name: payment.name.trim(),
            payment_amount: parseNumeric(payment.payment_amount),
            timestamp: payment.timestamp
            ? new Date(payment.timestamp).toISOString()
            : new Date().toISOString(), // 確保 timestamp 有值
            notes: payment.notes !== undefined ? payment.notes.trim() : "",
            transaction_amount: null,
            order_quantity: null,
            sum_price: null,
        };
        });

        // 傳送至後端
        const response = await fetch(`${apiUrl}/save-accounting/`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
            records: [...formattedDebts, ...formattedPayments],
        }),
        });

        if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save accounting data");
        }

        const result = await response.json();
        return { success: true, data: result };
    } catch (error) {
        console.error("Error saving accounting data:", error);
        return { success: false, error: error.message || "Failed to save accounting data" };
    }
    };

    // punch-in
    const fetchAllWorkHours = async () => {
        if (!authToken) {
            console.error("Access token not found. The user might not be logged in.");
            return { success: false, error: "User not authenticated" };
        }

        try {
            const response = await fetch(`${apiUrl}/work-hours/fetch/`, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`, // 傳入 Bearer Token
                },
            });

            const result = await response.json();

            if (response.ok && Array.isArray(result)) {
                // console.log('Fetched all work hours:', result);
                return { success: true, data: result };
            } else {
                console.error('Unexpected response format:', result);
                return { success: false, error: 'Unexpected response format' };
            }
        } catch (error) {
            console.error('Error fetching all work hours:', error);
            return { success: false, error: 'Error fetching all work hours' };
        }
    };

    const deleteWorkHourRecord = async (recordId) => {
        if (!authToken) {
            console.error("Access token not found. The user might not be logged in.");
            return { success: false, error: "User not authenticated" };
        }

        if (!recordId) {
            console.error("Record ID is missing. Ensure it is provided.");
            return { success: false, error: "Record ID is required" };
        }

        try {
            const response = await fetch(`${apiUrl}/work-hours/${recordId}/delete/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
            });

            if (response.ok) {
                const result = await response.json();
                console.log(`Record with ID ${recordId} deleted successfully:`, result);
                return { success: true, data: result };
            } else {
                const result = await response.json();
                console.error(`Failed to delete record with ID ${recordId}:`, result);
                return { success: false, error: result.error || 'Failed to delete the record' };
            }
        } catch (error) {
            console.error(`Error deleting record with ID ${recordId}:`, error);
            return { success: false, error: 'Error deleting the record' };
        }
    };

    const extractPdfText = async (file, mode = "fast", quality = "standard") => {
        return extractPdfTextRequest({ file, mode, quality, authToken });
    };

    const processAiText = async (
        text,
        workflow = "solution",
        options = {},
    ) => {
        return processAiTextRequest({
            text,
            workflow,
            authToken,
            model: options.model,
            verifyModel: options.verifyModel,
            verify: options.verify,
        });
    };

    const startAiProcessJob = async (
        text,
        workflow = "solution",
        options = {},
    ) => {
        return startAiProcessJobRequest({
            text,
            workflow,
            authToken,
            model: options.model,
            verifyModel: options.verifyModel,
            verify: options.verify,
        });
    };

    const getAiProcessJob = async (jobId) => {
        return getAiProcessJobRequest({ jobId, authToken });
    };

    const startPdfExtractJob = async (
        file,
        mode = "math_markdown",
        quality = "ultra",
        options = {},
    ) => {
        return startPdfExtractJobRequest({
            file,
            mode,
            quality,
            authToken,
            clientRequestId: options.clientRequestId,
        });
    };

    const getPdfExtractJob = async (jobId) => {
        return getPdfExtractJobRequest({ jobId, authToken });
    };

    const listPdfExtractJobs = async () => listPdfExtractJobsRequest({ authToken });
    const cancelPdfExtractJob = async (jobId, options = {}) => cancelPdfExtractJobRequest({
        jobId, authToken, clientRequestId: options.clientRequestId, keepalive: options.keepalive,
    });

    const startImageRedrawJob = async (file, instructions = "") => {
        return startImageRedrawJobRequest({ file, instructions, authToken });
    };

    const getImageRedrawJob = async (jobId) => {
        return getImageRedrawJobRequest({ jobId, authToken });
    };

    const startHandoutImportJob = async (file, quality = "ultra") => {
        return startHandoutImportJobRequest({
            file,
            quality,
            authToken,
        });
    };

    const getHandoutImportJob = async (jobId) => {
        return getHandoutImportJobRequest({ jobId, authToken });
    };

    const previewMatplotlibCode = async (code) => {
        return previewMatplotlibCodeRequest({ code, authToken });
    };

    const buildMathBankJson = async (
        text,
        gradeId,
        unitId,
        defaultType = "calculation",
        defaultDifficulty = "U",
    ) => {
        return buildMathBankJsonRequest({
            text,
            gradeId,
            unitId,
            defaultType,
            defaultDifficulty,
            authToken,
        });
    };

    const removeHandwriting = async (
        file,
        colorMode = "exam_auto",
        outputFormat = "pdf",
        strength = 2,
        manualRegions = [],
        options = {},
    ) => {
        return removeHandwritingRequest({
            file,
            colorMode,
            outputFormat,
            strength,
            manualRegions,
            authToken,
            signal: options.signal,
            onProgress: options.onProgress,
            imageOverlays: options.imageOverlays || [],
        });
    };

    const repairHandwritingImage = (image, instructions, options = {}) =>
        repairHandwritingImageRequest({ image, instructions, authToken, ...options });

    const previewHandwriting = async (
        file,
        colorMode = "exam_auto",
        strength = 2,
        manualRegions = [],
        maxPages = 80,
        options = {},
    ) => {
        return previewHandwritingRequest({
            file,
            colorMode,
            strength,
            manualRegions,
            maxPages,
            authToken,
            signal: options.signal,
            onProgress: options.onProgress,
        });
    };

    const parseWordDocument = async (
        file,
        includeAssets = true,
        mode = "fast",
    ) => {
        return parseWordDocumentRequest({ file, includeAssets, mode, authToken });
    };

    const fetchWordTemplates = async () => {
        return fetchWordTemplatesRequest({ authToken });
    };

    const generateWordDocument = async (document, filename, templateId, options = {}) => {
        return generateWordDocumentRequest({
            document,
            filename,
            templateId,
            ...(options.mixedVersion ? { mixedVersion: options.mixedVersion } : {}),
            ...(options.signal ? { signal: options.signal } : {}),
            authToken,
        });
    };

    const generateWordFromBank = async ({
        subject,
        questionIds,
        title,
        filename,
        templateId,
        examRange,
        mode,
        mixedVersion,
        sectionMode,
        handoutOptions,
        recordWorkspace,
        recordPayload,
        bilingualTranslations,
        signal,
        onProgress,
    }) => {
        return generateWordFromBankRequest({
            subject,
            questionIds,
            title,
            filename,
            templateId,
            examRange,
            mode,
            mixedVersion,
            sectionMode,
            handoutOptions,
            recordWorkspace,
            recordPayload,
            bilingualTranslations,
            signal,
            onProgress,
            authToken,
        });
    };

    const previewBilingualExam = async (options) => previewBilingualExamRequest({ ...options, authToken });

    const generateWordAnswerSheet = async (file, filename, rows = [], schoolLevel = "") => {
        return generateWordAnswerSheetRequest({
            file,
            filename,
            rows,
            schoolLevel,
            authToken,
        });
    };

    const previewWordAnswerSheet = async (file) => {
        return previewWordAnswerSheetRequest({
            file,
            authToken,
        });
    };

    const buildAiSolutionFromImages = async (
        files,
        { text = "", model = "", verifyModel = "", verify } = {},
    ) => {
        return buildAiSolutionFromImagesRequest({
            files,
            text,
            model,
            verifyModel,
            verify,
            authToken,
        });
    };

    const startAiSolutionFromImagesJob = async (
        files,
        { text = "", promptAssets = [], model = "", verifyModel = "", verify } = {},
    ) => {
        return startAiSolutionFromImagesJobRequest({
            files,
            text,
            promptAssets,
            model,
            verifyModel,
            verify,
            authToken,
        });
    };

    const getAiSolutionFromImagesJob = async (jobId) => {
        return getAiSolutionFromImagesJobRequest({ jobId, authToken });
    };

    const sendAiChatMessage = async (
        filesOrOptions,
        { text = "", messages = [], model = "" } = {},
    ) => {
        const isFileList =
            typeof FileList !== "undefined" && filesOrOptions instanceof FileList;
        const options = Array.isArray(filesOrOptions) || isFileList
            ? { files: filesOrOptions, text, messages, model }
            : (filesOrOptions || {});
        return sendAiChatMessageRequest({
            files: options.files,
            text: options.text || "",
            messages: options.messages || [],
            model: options.model || "",
            authToken,
        });
    };

    return (
        <AuthContext.Provider value={{
            authToken,
            isAuthenticated,

            login,
            logout,

            sendTodoNoteData,
            fetchTodoNoteData,
            jumpTodoHistory,

            saveBulletin,
            getBulletins,
            deleteBulletin,

            fetchAccountingData,
            sendAccountingData,

            fetchAllWorkHours,
            deleteWorkHourRecord,

            extractPdfText,
            processAiText,
            startAiProcessJob,
            getAiProcessJob,
            startPdfExtractJob,
            getPdfExtractJob,
            listPdfExtractJobs,
            cancelPdfExtractJob,
            startImageRedrawJob,
            getImageRedrawJob,
            startHandoutImportJob,
            getHandoutImportJob,
            buildAiSolutionFromImages,
            startAiSolutionFromImagesJob,
            getAiSolutionFromImagesJob,
            sendAiChatMessage,
            previewMatplotlibCode,
            buildMathBankJson,
            removeHandwriting,
            previewHandwriting,
            repairHandwritingImage,
            parseWordDocument,
            fetchWordTemplates,
            generateWordDocument,
            generateWordFromBank,
            generateWordAnswerSheet,
            previewWordAnswerSheet,
            previewBilingualExam,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export const handleLike = async () => {
  try {
    const response = await fetch(`${apiUrl}/like/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();

    if (response.ok) {
      // Check if the like has already been made
      if (data.message === 'You have already liked this!') {
        return { success: false, message: data.message, total_likes: data.total_likes };
      }
      // Successful like
      return { success: true, data };
    } else {
      return { success: false, error: data.error || 'An unknown error occurred' };
    }
  } catch (error) {
    console.error('Error:', error);
    return { success: false, error: 'Network error' };
  }
};

export const handleView = async () => {
    try {
        const response = await fetch(`${apiUrl}/visit/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        const data = await response.json();
        console.log(data);
        
    } catch (error) {
        console.error('Error:', error);
    }
};
